/** @jsx jsx */
import { jsx, Flex, Box, Styled, Embed } from "theme-ui"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import Img from "gatsby-image"

import Layout, { Container, Row, Col } from "components/layout"
import { Large, Medium } from "components/type"
import Lines from "components/lines"
import LocalizedButton from "components/localized-button"
import Separator from "components/separator"

import ProgramTabs from "components/program-tabs"

import useLocales from "hooks/use-locales"

const IndexPage = ({ data }) => {
  const { t } = useLocales()
  return (
    <Layout pageTitle={t("title")} description={t("description")}>
      <Box sx={{ position: "relative", pb: 5, pt: 6 }}>
        <Container>
          <Img fluid={data.hero.childImageSharp.fluid} alt="" />
        </Container>
        <Box
          sx={{
            position: ["relative", "relative", "absolute"],
            width: ["95%", "90%", "50%"],
            bottom: ["auto", "auto", 6],
            left: 0,
          }}
        >
          <Box
            sx={{
              borderTopRightRadius: "default",
              borderBottomRightRadius: "default",
              bg: "background",
              boxShadow: "default",
              mb: 4,
              p: 3,
              pl: [3, 5, 6],
            }}
          >
            <Img fluid={data.logo.childImageSharp.fluid} alt="" />
          </Box>
          <Box
            sx={{
              borderTopRightRadius: "default",
              borderBottomRightRadius: "default",
              bg: "primary",
              boxShadow: "default",
              p: 3,
            }}
          >
            <Styled.h1 sx={{ color: "background", my: 0, textAlign: "right" }}>
              {t("title")}
            </Styled.h1>
          </Box>
        </Box>
      </Box>
      <Container>
        <Large sx={{ fontWeight: 500, textAlign: "center" }}>
          {t("tagline")}
        </Large>
      </Container>
      <Lines side="left" />
      <Flex sx={{ alignItems: "center", justifyContent: "center" }}>
        <Box
          sx={{
            bg: "primary",
            borderRadius: "default",
            color: "background",
            p: 4,
            textAlign: "center",
            width: ["90%", "80%", "60%"],
          }}
        >
          <Styled.h2 sx={{ color: "background", mt: 0 }}>
            {t("mission.title")}
          </Styled.h2>
          <Styled.p>{t("mission.text")}</Styled.p>
          <Styled.h2 sx={{ color: "background" }}>
            {t("vision.title")}
          </Styled.h2>
          <Styled.p sx={{ mb: 0 }}>{t("vision.text")}</Styled.p>
        </Box>
      </Flex>
      <Lines side="right" />
      <Container>
        <Styled.h2 sx={{ mb: 4, color: "primary" }}>
          {t("message.title")}
        </Styled.h2>
        <Styled.p>{t("message.p.1")}</Styled.p>
        <Styled.p>{t("message.p.2")}</Styled.p>
        <Styled.p>{t("message.p.3")}</Styled.p>
        <Styled.p>{t("message.p.4")}</Styled.p>
        <Styled.p>{t("message.p.5")}</Styled.p>
        <Row sx={{ flexWrap: "wrap", mt: 4 }}>
          <Col sx={{ display: "flex", flexDirection: "column" }}>
            <Img fixed={data.barb.childImageSharp.fixed} sx={{ mb: 3 }} />
            <Img fixed={data.barbSig.childImageSharp.fixed} />
            <Styled.h3 as="p">Barbara Paquette</Styled.h3>
            <Styled.p sx={{ mt: 0 }}>{t("president")}</Styled.p>
          </Col>
          <Col sx={{ display: "flex", flexDirection: "column" }}>
            <Img fixed={data.nathalie.childImageSharp.fixed} sx={{ mb: 3 }} />
            <Img fixed={data.nathalieSig.childImageSharp.fixed} />
            <Styled.h3 as="p">Nathalie Lafrenière</Styled.h3>
            <Styled.p sx={{ mt: 0 }}>{t("ed")}</Styled.p>
          </Col>
        </Row>
      </Container>
      <ProgramTabs />
      <Container>
        <Separator direction="left" />
        <Row sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Col sx={{ width: ["100%", "50%"] }}>
            <Styled.h2>{t("volunteer.title")}</Styled.h2>
            <Medium>
              {t("volunteer.year")}
              <br />
              {t("volunteer.month")}
              <br />
              {t("volunteer.week")}
            </Medium>
            <Large sx={{ color: "primary", mb: 4 }}>{t("volunteer.callout")}</Large>
            <Styled.p>
              <LocalizedButton to="/volunteers">{t("volunteer.button")}</LocalizedButton>
            </Styled.p>
          </Col>
          <Col sx={{ width: ["100%", "50%"] }}>
            <Img
              fluid={data.volunteers.childImageSharp.fluid}
              sx={{ borderRadius: "default" }}
            />
          </Col>
        </Row>
        <Separator direction="right" />
      </Container>
      <Embed sx={{ my: 5 }} src="https://www.youtube.com/embed/2kEhuDyO5Jw" />
      <Container>
        <Separator direction="right" />
        <Row sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Col sx={{ width: ["100%", "50%"] }}>
            <Styled.h2>{t("funding.title")}</Styled.h2>
            <Medium sx={{ mb: 4 }}>
              {t("funding.text")} <strong>{t("thanks")}</strong>
            </Medium>
            <Styled.p>
              <LocalizedButton to="/funding">{t("funding.button")}</LocalizedButton>
            </Styled.p>
          </Col>
          <Col sx={{ width: ["100%", "50%"] }}>
            <Img
              fluid={data.funding.childImageSharp.fluid}
              sx={{ borderRadius: "default" }}
            />
          </Col>
        </Row>
        <Separator direction="left" />
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query IndexPageQuery {
    hero: file(relativePath: { eq: "home-hero.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    logo: file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fluid(maxWidth: 600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    barb: file(relativePath: { eq: "barb.jpg" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    nathalie: file(relativePath: { eq: "nathalie.jpg" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    barbSig: file(relativePath: { eq: "barb-signature.png" }) {
      childImageSharp {
        fixed(width: 220) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    nathalieSig: file(relativePath: { eq: "nathalie-signature.png" }) {
      childImageSharp {
        fixed(width: 220) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    volunteers: file(relativePath: { eq: "home-volunteers.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 715) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    funding: file(relativePath: { eq: "home-funding.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 715) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`

IndexPage.propTypes = {
  data: PropTypes.any,
}

export default IndexPage
